import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  company: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

const Admin: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchMessages();
  }, [isAuthenticated, navigate]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      const response = await axios.get<ContactMessage[]>(`${API_BASE_URL}/api/admin/messages`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setMessages(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch messages');
      if (err.response?.status === 403 || err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      await axios.put(
        `${API_BASE_URL}/api/admin/messages/${messageId}/read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setMessages(messages.map(msg => msg.id === messageId ? { ...msg, isRead: true } : msg));
      if (selectedMessage?.id === messageId) {
        setSelectedMessage({ ...selectedMessage, isRead: true });
      }
    } catch (err: any) {
      console.error('Failed to mark message as read:', err);
    }
  };

  const unreadCount = messages.filter(msg => !msg.isRead).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 text-white overflow-hidden relative">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-blue-500/30 to-cyan-500/30 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-32 right-16 w-96 h-96 bg-gradient-to-r from-purple-500/30 to-pink-500/30 rounded-full blur-3xl animate-float-delayed"></div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-white/5 backdrop-blur-xl border-b border-white/10 sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 via-cyan-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-xl group-hover:shadow-2xl group-hover:shadow-blue-500/25 transition-all duration-300 group-hover:scale-105">
                <span className="text-white font-bold text-xl">A</span>
              </div>
              <span className="text-3xl font-black bg-gradient-to-r from-white via-blue-100 to-cyan-200 bg-clip-text text-transparent tracking-tight">Alenis</span>
            </Link>
            <div className="flex items-center space-x-6">
              <LanguageSwitcher />
              <Link to="/dashboard" className="px-6 py-3 text-white/80 hover:text-white transition-all duration-300 hover:bg-white/10 rounded-xl backdrop-blur-sm">
                Dashboard
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <section className="relative z-10 pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-8">
            <h1 className="text-5xl font-black mb-4 bg-gradient-to-r from-white via-blue-100 to-cyan-200 bg-clip-text text-transparent">
              Admin Panel
            </h1>
            <p className="text-xl text-slate-300">Manage contact messages</p>
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 mb-6">
              <p className="text-red-300">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              <p className="mt-4 text-slate-300">Loading messages...</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Messages List */}
              <div className="lg:col-span-2 space-y-4">
                <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-3xl p-6 border border-white/10">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-white">
                      Contact Messages
                      {unreadCount > 0 && (
                        <span className="ml-3 px-3 py-1 bg-red-500/20 border border-red-400/30 rounded-full text-red-300 text-sm">
                          {unreadCount} unread
                        </span>
                      )}
                    </h2>
                    <button
                      onClick={fetchMessages}
                      className="px-4 py-2 bg-blue-600/20 border border-blue-400/30 rounded-xl text-blue-300 hover:bg-blue-600/30 transition-all"
                    >
                      Refresh
                    </button>
                  </div>

                  {messages.length === 0 ? (
                    <div className="text-center py-12">
                      <svg className="w-16 h-16 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                      <p className="text-slate-400">No messages yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          onClick={() => {
                            setSelectedMessage(message);
                            if (!message.isRead) {
                              markAsRead(message.id);
                            }
                          }}
                          className={`p-4 rounded-xl border cursor-pointer transition-all ${
                            message.isRead
                              ? 'bg-slate-800/30 border-slate-700/50'
                              : 'bg-blue-500/10 border-blue-400/30'
                          } ${selectedMessage?.id === message.id ? 'ring-2 ring-blue-500' : ''}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-bold text-white">{message.name}</h3>
                                {!message.isRead && (
                                  <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                                )}
                              </div>
                              <p className="text-sm text-slate-400 mb-1">{message.email}</p>
                              {message.company && (
                                <p className="text-sm text-slate-400 mb-2">{message.company}</p>
                              )}
                              <p className="text-slate-300 line-clamp-2">{message.message}</p>
                              <p className="text-xs text-slate-500 mt-2">
                                {new Date(message.createdAt).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Message Detail */}
              <div className="lg:col-span-1">
                {selectedMessage ? (
                  <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-3xl p-6 border border-white/10 sticky top-24">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-white">Message Details</h3>
                      {!selectedMessage.isRead && (
                        <button
                          onClick={() => markAsRead(selectedMessage.id)}
                          className="px-3 py-1 bg-green-600/20 border border-green-400/30 rounded-lg text-green-300 text-sm hover:bg-green-600/30 transition-all"
                        >
                          Mark as Read
                        </button>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-slate-400">Name</label>
                        <p className="text-white font-semibold">{selectedMessage.name}</p>
                      </div>
                      <div>
                        <label className="text-sm text-slate-400">Email</label>
                        <p className="text-white">
                          <a href={`mailto:${selectedMessage.email}`} className="text-blue-400 hover:text-blue-300">
                            {selectedMessage.email}
                          </a>
                        </p>
                      </div>
                      {selectedMessage.company && (
                        <div>
                          <label className="text-sm text-slate-400">Company</label>
                          <p className="text-white">{selectedMessage.company}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-sm text-slate-400">Message</label>
                        <p className="text-white leading-relaxed whitespace-pre-wrap">{selectedMessage.message}</p>
                      </div>
                      <div>
                        <label className="text-sm text-slate-400">Received</label>
                        <p className="text-white text-sm">
                          {new Date(selectedMessage.createdAt).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm text-slate-400">Status</label>
                        <p className={`text-sm ${selectedMessage.isRead ? 'text-green-400' : 'text-blue-400'}`}>
                          {selectedMessage.isRead ? 'Read' : 'Unread'}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-3xl p-6 border border-white/10 sticky top-24">
                    <p className="text-slate-400 text-center py-8">
                      Select a message to view details
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Admin;

